import { useQueries } from "@tanstack/react-query";

import { getCurrentVersionV2QueryOptions } from "../api/generated/provisioning-downloads/provisioning-downloads";
import useProvisionerProducts from "./useProvisionerProducts";

const EMPTY_VERSIONS = Object.freeze( [] );

/**
 * The current-version query for one product code, answering a 404 with no versions.
 *
 * The endpoint answers 404 for a product without downloadable releases, which is an empty list, not a
 * failure to load one.
 *
 * @param {string} productCode The product code.
 *
 * @returns {Object} The query options.
 */
function currentVersionQuery( productCode ) {
	const options = getCurrentVersionV2QueryOptions( { productCode } );

	return {
		...options,
		queryFn: async( context ) => {
			try {
				return await options.queryFn( context );
			} catch ( error ) {
				if ( error?.response?.status === 404 ) {
					return { versions: EMPTY_VERSIONS };
				}
				throw error;
			}
		},
	};
}

/**
 * The latest plugin versions per product code the provisioner holds: the product list, then one
 * `GET v2/provisioning/downloads/currentVersion` per code.
 *
 * One request per code is the endpoint's shape; a provisioner holds a handful of codes, so this stays
 * a handful of requests.
 *
 * @returns {{downloads: Object[], isLoading: boolean, isError: boolean}} One entry per product with its
 *          own `isLoading` and `isError`; the top-level flags describe the product list.
 */
export default function useProvisioningDownloads() {
	const { products, isLoading, isError } = useProvisionerProducts();

	const versionQueries = useQueries( {
		queries: products.map( product => currentVersionQuery( product.productCode ) ),
	} );

	const downloads = products.map( ( product, index ) => ( {
		productCode: product.productCode,
		name: product.name,
		versions: versionQueries[ index ].data?.versions ?? EMPTY_VERSIONS,
		isLoading: versionQueries[ index ].isLoading,
		isError: versionQueries[ index ].isError,
	} ) );

	return { downloads, isLoading, isError };
}
